import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, NavLink, useFetcher, useNavigate, useRouteLoaderData } from 'react-router-dom';
import { ADMIN_ROLE, SUPERADMIN_ROLE, api, hasRole } from '../../services/api';
import type { Principal } from '../../services/api';
import { clearPrincipalCache, onConversationsChanged } from '../../services/session';
import { cx } from '../../utils/cx';
import { BrandMark } from '../../components/BrandMark';
import { CloseIcon, DocumentIcon, PlusIcon, UsersIcon } from '../../components/icons';
import { SystemStatus } from '../SystemStatus';
import styles from './Sidebar.module.css';

// Ресурс-маршрут: список перечитується без переходу між сторінками.
export const CONVERSATIONS_ROUTE = '/conversations';

const CONVERSATIONS_TAKE = 30;

// `failed`, а не кинута помилка: та підняла б errorElement і замінила весь екран.
export interface ConversationListView {
  items: ReadonlyArray<{ id: string; title: string }>;
  failed: boolean;
}

export async function conversationsLoader(): Promise<ConversationListView> {
  try {
    const page = await api.listConversations({ take: CONVERSATIONS_TAKE });
    return { items: page.items, failed: false };
  } catch {
    return { items: [], failed: true };
  }
}

export function Sidebar({ onClose }: { onClose?: () => void }) {
  const navigate = useNavigate();
  const { principal } = useRouteLoaderData('shell') as { principal: Principal };
  const [leaving, setLeaving] = useState(false);

  const isAdmin = hasRole(principal, ADMIN_ROLE);
  const isSuperadmin = hasRole(principal, SUPERADMIN_ROLE);
  // Пошта — фолбек лише для показу; у поля, що зберігаються назавжди, вона не йде.
  const name = principal.displayName ?? principal.email ?? 'Користувач';
  const initials = name.slice(0, 2).toUpperCase();

  // У SERVICE-принципала своїх розмов немає — сервер відповів би 403.
  const showConversations = principal.kind === 'USER';
  const conversations = useFetcher<ConversationListView>();
  const list = conversations.data;
  const hint = listHint(list);

  // `fetcher.load` — нова функція на кожен рендер, тому через ref.
  const loadRef = useRef(conversations.load);
  loadRef.current = conversations.load;

  useEffect(() => {
    if (!showConversations) {
      return;
    }
    void loadRef.current(CONVERSATIONS_ROUTE);
    return onConversationsChanged(() => {
      void loadRef.current(CONVERSATIONS_ROUTE);
    });
  }, [showConversations]);

  async function onLogout(): Promise<void> {
    if (leaving) {
      return;
    }
    setLeaving(true);
    try {
      await api.logout();
    } finally {
      // Скидаємо навіть після невдалого запиту.
      clearPrincipalCache();
      navigate('/login', { replace: true });
    }
  }

  return (
    <aside className={styles.side}>
      <div className={styles.brand}>
        <BrandMark size={28} className={styles.mark} />
        <span className={styles.brandText}>Внутрішній помічник</span>
        {/* На широкому екрані кнопку ховає CSS: боковина там постійна. */}
        {onClose ? (
          <button
            type="button"
            className={styles.close}
            aria-label="Закрити меню"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        ) : null}
      </div>

      <div className={styles.scroll}>
        {/* Корінь і є новим чатом: адреса зʼявляється після першої відповіді. */}
        <Link className={styles.item} to="/">
          <PlusIcon />
          <span className={styles.label}>Новий діалог</span>
        </Link>

        <nav className={styles.nav} aria-label="Розділи">
          <div className={styles.group}>Розділи</div>

          {SECTIONS.filter(
            (section) =>
              (!section.adminOnly || isAdmin) && (!section.superadminOnly || isSuperadmin),
          ).map((section) => (
            <NavLink key={section.to} to={section.to} className={itemClasses}>
              {section.icon}
              <span className={styles.label}>{section.label}</span>
            </NavLink>
          ))}
        </nav>

        {showConversations ? (
          <nav className={styles.nav} aria-label="Мої розмови">
            <div className={styles.group}>Розмови</div>

            {hint !== null ? <p className={styles.note}>{hint}</p> : null}

            {/* Повний текст у `title`: рядок обрізає ширина панелі. */}
            {list?.items.map((conversation) => (
              <NavLink
                key={conversation.id}
                to={`/c/${conversation.id}`}
                className={recentClasses}
                title={conversation.title}
              >
                {conversation.title}
              </NavLink>
            ))}
          </nav>
        ) : null}
      </div>

      <div className={styles.foot}>
        <div className={styles.status}>
          <SystemStatus />
        </div>

        <button type="button" className={styles.user} onClick={onLogout} disabled={leaving}>
          <span className={styles.avatar}>{initials}</span>
          <span className={styles.userText}>
            <span className={styles.userName}>{name}</span>
            <span className={styles.userHint}>{leaving ? 'Виходимо…' : 'Вийти'}</span>
          </span>
        </button>
      </div>
    </aside>
  );
}

function itemClasses({ isActive }: { isActive: boolean }): string {
  return cx(styles.item, isActive && styles.active);
}

function recentClasses({ isActive }: { isActive: boolean }): string {
  return cx(styles.recent, isActive && styles.recentActive);
}

function ChatIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
      <path d="M20 12a8 8 0 0 1-11.6 7.1L4 20l.9-4.4A8 8 0 1 1 20 12z" strokeLinejoin="round" />
    </svg>
  );
}

function PowerIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
      <path d="M12 3.5v7.5" strokeLinecap="round" />
      <path d="M7.6 6.6a7 7 0 1 0 8.8 0" strokeLinecap="round" />
    </svg>
  );
}

function QuestionIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
      <path d="M9.1 9a3 3 0 1 1 4.5 2.6c-.9.6-1.6 1.1-1.6 2.4" />
      <circle cx="12" cy="18" r="0.7" fill="currentColor" stroke="none" />
    </svg>
  );
}

// `adminOnly` — це UX, не захист: перевіряють бекенд і лоадер маршруту.
const SECTIONS: ReadonlyArray<{
  to: string;
  label: string;
  icon: ReactNode;
  adminOnly?: boolean;
  superadminOnly?: boolean;
}> = [
  { to: '/', label: 'Чат', icon: <ChatIcon /> },
  { to: '/knowledge', label: 'Документи', icon: <DocumentIcon />, adminOnly: true },
  { to: '/unanswered', label: 'Без відповіді', icon: <QuestionIcon />, adminOnly: true },
  { to: '/bot', label: 'Стан бота', icon: <PowerIcon />, adminOnly: true },
  { to: '/users', label: 'Доступи', icon: <UsersIcon />, superadminOnly: true },
];

// Без прикладів питань: у списку вони читалися б як історія, якої не було.
function listHint(list: ConversationListView | undefined): string | null {
  if (list === undefined) {
    return 'Завантажуємо…';
  }
  if (list.failed) {
    return 'Розмови не завантажились.';
  }
  return list.items.length === 0 ? 'Тут зʼявляться ваші розмови.' : null;
}
